import { useState } from 'react';
import SEO, { PAGE_SEO } from '../components/SEO';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, Sparkles, Gift } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password })
      });

      if (!response.ok) {
        if (response.status === 400) {
          throw new Error('Email already registered or invalid input');
        } else {
          throw new Error('Registration failed. Please try again.');
        }
      }

      const data = await response.json();
      login(data.user, data.token);
      toast.success('Welcome to SkinUse! You earned 100 SkinPoints!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    try {
      localStorage.removeItem('skinuse_token');
    } catch (e) {
      // Ignore localStorage errors on mobile
    }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/dashboard')}`;
  };

  return (
    <div className="min-h-screen flex bg-background">
      <SEO {...PAGE_SEO.register} url="/register" />
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-lg">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-10 transition-colors group" 
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to home
          </Link>

          {/* Logo and Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-8">
              <span className="font-serif text-3xl font-semibold">SkinUse</span>
              <span className="w-1.5 h-1.5 bg-gold rounded-full"></span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold mb-3">Create Account</h1>
            <p className="text-muted-foreground text-lg">Start your personalized beauty journey</p>
          </div>

          {/* Welcome bonus banner */}
          <div className="flex items-center gap-4 p-5 bg-gradient-to-r from-gold/15 to-gold/5 rounded-xl mb-10 border border-gold/20">
            <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
              <Gift className="w-6 h-6 text-gold" />
            </div>
            <div>
              <p className="font-semibold text-base">Welcome Bonus</p>
              <p className="text-sm text-muted-foreground">Get 100 SkinPoints when you sign up!</p>
            </div>
            <Sparkles className="w-5 h-5 text-gold ml-auto" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-7">
            <div className="space-y-3">
              <Label htmlFor="name" className="text-base font-medium">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-14 text-base px-4 rounded-lg border-border/50 focus:border-gold transition-colors"
                placeholder="Your name"
                required
                data-testid="register-name"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="email" className="text-base font-medium">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-14 text-base px-4 rounded-lg border-border/50 focus:border-gold transition-colors"
                placeholder="you@example.com"
                required
                data-testid="register-email"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="password" className="text-base font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-14 text-base px-4 pr-12 rounded-lg border-border/50 focus:border-gold transition-colors"
                  placeholder="Create a strong password"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
            </div>

            <Button 
              type="submit" 
              className="w-full h-14 text-base font-semibold rounded-lg bg-gold hover:bg-gold/90 text-black transition-all" 
              disabled={loading} 
              data-testid="register-submit"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-10">
            <Separator className="bg-border/50" />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-6 text-sm text-muted-foreground uppercase tracking-widest">
              or
            </span>
          </div>

          {/* Google Login */}
          <Button
            variant="outline"
            className="w-full h-14 text-base font-semibold rounded-lg border-border/50 hover:bg-secondary/50 transition-all"
            onClick={handleGoogleLogin}
            data-testid="google-register"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Login Link */}
          <p className="text-center text-base text-muted-foreground mt-10">
            Already have an account?{' '}
            <Link to="/login" className="text-gold hover:underline font-semibold" data-testid="go-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/30797176/pexels-photo-30797176.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt="Beauty"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-black/70 to-black/40" />
        <div className="absolute bottom-16 right-16 left-16 text-white text-right">
          <h2 className="font-serif text-5xl font-semibold mb-6 leading-tight">Join SkinUse</h2>
          <p className="text-white/80 text-lg max-w-lg ml-auto leading-relaxed">
            Get personalized advice, earn rewards, and discover products perfect for your unique skin and hair needs.
          </p>
        </div>
      </div>
    </div>
  );
}
